import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import type { FeatureCollection } from "geojson";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { MarkerData } from "common/components/Map/MarkerData";
import { useAppSelector } from "store/store";

import { MapContext } from "./MapContext";

interface IProps {
	points: MarkerData[];
	type: "estadoITE" | "indiceNEC";
	children: React.ReactNode;
	mapOnClick: () => void;
}

const calcZoom = (width: number) => {
	if (width < 415) return 4.25; // Mobile
	if (width < 830) return 5.25; // Tablet
	if (width < 2500) return 5.5; // Desktop
	if (width < 3900) return 6.5; // Large Screens
	return 1;
};

const calcInitialLatitude = (width: number) => {
	if (width < 415) return 42.5;
	if (width < 830) return 41;
	if (width < 2500) return 40.416775;
	if (width < 3900) return 40.416775;
	return 40.416775;
};

const calcInitialLongitude = (width: number) => {
	if (width < 415) return -3;
	if (width < 830) return -3.5;
	if (width < 2500) return -5.25;
	if (width < 3900) return -5.25;
	return -5.25;
};

const getStyleUrl = (style: string) => {
	switch (style) {
		case "satellite":
			return "mapbox://styles/mapbox/satellite-v9";
		default:
			return "mapbox://styles/dhub/cl2ypoavr000z14n5af53rj77";
	}
};

// Hex equivalentes a los colores de Chakra UI
// (las CSS variables var(--chakra-colors-*) no funcionan en el canvas WebGL de Mapbox)
const COLOR_ITE: Record<string, string> = {
	Rechazado: "#F56565", // red-400
	Entregado: "#ECC94B", // yellow-400
	Registrado: "#48BB78", // green-400
	Reenviado: "#DD6B20", // orange-500
};

const COLOR_NEC: Record<string, string> = {
	NCE4: "#9B2C2C", // red-700
	NCE2: "#ECC94B", // yellow-400
	NCE1: "#48BB78", // green-400
	NCE3: "#F6AD55", // orange-300
};

const DEFAULT_COLOR = "#718096"; // gray-500

// Todos los colores únicos posibles
const ALL_COLORS = Array.from(new Set([...Object.values(COLOR_ITE), ...Object.values(COLOR_NEC), DEFAULT_COLOR]));

const MARKERS_LAYER_ID = "markers-layer";

function colorToImageId(color: string): string {
	return `pin-${color.replace("#", "")}`;
}

function createMarkerDataURL(color: string): string {
	// SVG exacto del <Marker> de Mapbox GL, sustituyendo el fill por el color deseado
	const svg = `<svg display="block" height="41px" width="27px" viewBox="0 0 27 41" xmlns="http://www.w3.org/2000/svg">
		<defs>
			<radialGradient id="shadowGradient">
				<stop offset="10%" stop-opacity="0.4"></stop>
				<stop offset="100%" stop-opacity="0.05"></stop>
			</radialGradient>
		</defs>
		<ellipse cx="13.5" cy="34.8" rx="10.5" ry="5.25" fill="url(#shadowGradient)"></ellipse>
		<path fill="${color}" d="M27,13.5C27,19.07 20.25,27 14.75,34.5C14.02,35.5 12.98,35.5 12.25,34.5C6.75,27 0,19.22 0,13.5C0,6.04 6.04,0 13.5,0C20.96,0 27,6.04 27,13.5Z"></path>
		<path opacity="0.25" d="M13.5,0C6.04,0 0,6.04 0,13.5C0,19.22 6.75,27 12.25,34.5C13,35.52 14.02,35.5 14.75,34.5C20.25,27 27,19.07 27,13.5C27,6.04 20.96,0 13.5,0ZM13.5,1C20.42,1 26,6.58 26,13.5C26,15.9 24.5,19.18 22.22,22.74C19.95,26.3 16.71,30.14 13.94,33.91C13.74,34.18 13.61,34.32 13.5,34.44C13.39,34.32 13.26,34.18 13.06,33.91C10.28,30.13 7.41,26.31 5.02,22.77C2.62,19.23 1,15.95 1,13.5C1,6.58 6.58,1 13.5,1Z"></path>
		<circle fill="white" cx="13.5" cy="13.5" r="5.5"></circle>
	</svg>`;
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

// Expresión Mapbox GL para elegir el icono según el color del feature
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_IMAGE_EXPR: any = [
	"match",
	["get", "color"],
	...ALL_COLORS.flatMap((c) => [c, colorToImageId(c)]),
	colorToImageId(DEFAULT_COLOR), // fallback
];

const LAYER_STYLE: mapboxgl.SymbolLayer = {
	id: MARKERS_LAYER_ID,
	type: "symbol",
	source: "markers-source",
	layout: {
		"icon-image": ICON_IMAGE_EXPR,
		"icon-size": 0.6,
		"icon-anchor": "bottom",
		"icon-allow-overlap": true,
	},
};

const buildGeoJSON = (points: MarkerData[], viewStats: "estadoITE" | "indiceNEC"): FeatureCollection => ({
	type: "FeatureCollection",
	features: points.map((p) => ({
		type: "Feature",
		geometry: { type: "Point", coordinates: [p.longitude, p.latitude] },
		properties: {
			id: p.id,
			color:
				viewStats === "estadoITE" ? COLOR_ITE[p.estadoITE] ?? DEFAULT_COLOR : COLOR_NEC[p.indiceNEC] ?? DEFAULT_COLOR,
		},
	})),
});

export const MapboxPortfolioComponent: React.FC<IProps> = (props) => {
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
	const mapStyle = useAppSelector((state) => state.user.mapStyle);
	const mapViewStats = useAppSelector((state) => state.portfolio.mapViewStats);

	// Refs estables para evitar dependencias en los handlers de eventos
	const pointsRef = useRef(props.points);
	pointsRef.current = props.points;

	const mapOnClickRef = useRef(props.mapOnClick);
	mapOnClickRef.current = props.mapOnClick;

	// Memoizado: solo se recalcula cuando cambian los puntos o la vista de stats
	const geoJSON = useMemo(() => buildGeoJSON(props.points, mapViewStats), [props.points, mapViewStats]);
	const geoJSONRef = useRef(geoJSON);
	geoJSONRef.current = geoJSON;

	const loadImages = useCallback((map: mapboxgl.Map) => {
		ALL_COLORS.forEach((color) => {
			const imageId = colorToImageId(color);
			if (map.hasImage(imageId)) return;
			const img = new Image(27, 41);
			img.onload = () => {
				if (!map.hasImage(imageId)) map.addImage(imageId, img);
			};
			img.src = createMarkerDataURL(color);
		});
	}, []);

	const addSourceAndLayer = useCallback((map: mapboxgl.Map) => {
		if (!map.getSource("markers-source")) {
			map.addSource("markers-source", { type: "geojson", data: geoJSONRef.current });
		}
		if (!map.getLayer(MARKERS_LAYER_ID)) {
			map.addLayer(LAYER_STYLE);
		}
	}, []);

	// Inicializar mapa
	useEffect(() => {
		if (!mapContainerRef.current) return;

		const map = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: getStyleUrl(mapStyle),
			accessToken: "pk.eyJ1IjoiZGh1YiIsImEiOiJjbGUyczIyODQwMXdrM3dseG53MnMxaXI4In0.FYGuulRoBvKZ56vsbIXpzA",
			zoom: calcZoom(window.innerWidth),
			center: [calcInitialLongitude(window.innerWidth), calcInitialLatitude(window.innerWidth)],
		});

		mapRef.current = map;

		map.on("load", () => {
			loadImages(map);
			addSourceAndLayer(map);
			setMapInstance(map);
		});

		// "styleimagemissing": Mapbox pide una imagen que no existe → la cargamos al momento
		map.on("styleimagemissing", (e) => {
			const color = ALL_COLORS.find((c) => colorToImageId(c) === e.id);
			if (color) {
				const imageId = colorToImageId(color);
				if (map.hasImage(imageId)) return;
				const img = new Image(27, 41);
				img.onload = () => {
					if (!map.hasImage(imageId)) map.addImage(imageId, img);
				};
				img.src = createMarkerDataURL(color);
			}
		});

		// "style.load": el estilo se ha recargado (cambio mapa/satélite) → recargamos imágenes y capas
		map.on("style.load", () => {
			loadImages(map);
			addSourceAndLayer(map);
		});

		map.on("click", (e) => {
			const features = map.queryRenderedFeatures(e.point, { layers: [MARKERS_LAYER_ID] });
			if (features.length > 0) {
				const id = features[0].properties?.id;
				pointsRef.current.find((p) => p.id === id)?.onClick?.();
			} else {
				mapOnClickRef.current();
			}
		});

		map.on("mousemove", (e) => {
			const features = map.queryRenderedFeatures(e.point, { layers: [MARKERS_LAYER_ID] });
			map.getCanvas().style.cursor = features.length > 0 ? "pointer" : "";
		});

		map.on("mouseleave", () => {
			map.getCanvas().style.cursor = "";
		});

		const handleResize = () => setTimeout(() => map.resize(), 1000);
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			map.remove();
			mapRef.current = null;
			setMapInstance(null);
		};
	}, []);

	// Actualizar estilo cuando cambia mapStyle
	useEffect(() => {
		mapRef.current?.setStyle(getStyleUrl(mapStyle));
	}, [mapStyle]);

	// Actualizar datos GeoJSON cuando cambian los puntos o la vista
	useEffect(() => {
		const source = mapRef.current?.getSource("markers-source") as mapboxgl.GeoJSONSource | undefined;
		source?.setData(geoJSON);
	}, [geoJSON]);

	return (
		<MapContext.Provider value={mapInstance}>
			<Box
				width="100%"
				height="100%"
				id="map-container"
				position="absolute"
				top="0"
				left="0"
				zIndex="0"
				pointerEvents="auto"
			>
				<div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />
				{props.children}
			</Box>
		</MapContext.Provider>
	);
};
